// src/pages/CRM/Core/components/ContactRelationships.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, IconButton, Chip, Avatar,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem,
    List, ListItem, ListItemAvatar, ListItemText, ListItemSecondaryAction,
    Alert, CircularProgress, Divider, Tooltip, Autocomplete
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Search as SearchIcon,
    Person as PersonIcon,
    Link as LinkIcon,
    SwapHoriz as SwapIcon
} from '@mui/icons-material';
import axiosInstance from '../../../../../utils/axiosInstance';

const RelationshipType = {
    ASSISTANT_TO: 'ASSISTANT_TO',
    REPORTS_TO: 'REPORTS_TO',
    SPOUSE_OF: 'SPOUSE_OF',
    COLLEAGUE: 'COLLEAGUE',
    REFERRED_BY: 'REFERRED_BY',
    OTHER: 'OTHER'
};

const RelationshipTypeLabels = {
    ASSISTANT_TO: 'Assistant To',
    REPORTS_TO: 'Reports To',
    SPOUSE_OF: 'Spouse Of',
    COLLEAGUE: 'Colleague',
    REFERRED_BY: 'Referred By',
    OTHER: 'Other'
};

const ContactRelationships = ({ contactId, canEdit, onNavigateToContact }) => {
    const [relationships, setRelationships] = useState({ from_contact: [], to_contact: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Dialog states
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedRelationship, setSelectedRelationship] = useState(null);
    
    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        related_contact_id: null,
        relationship_type: RelationshipType.COLLEAGUE,
        description: '',
        create_reciprocal: false,
        reciprocal_type: ''
    });
    const [submitting, setSubmitting] = useState(false);

    const fetchRelationships = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/api/crm/contacts/${contactId}/relationships`);
            if (response.data.success) {
                setRelationships(response.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load relationships');
        } finally {
            setLoading(false);
        }
    }, [contactId]);

    useEffect(() => {
        fetchRelationships();
    }, [fetchRelationships]);

    const searchContacts = async (query) => {
        if (query.length < 2) {
            setSearchResults([]);
            return;
        }
        
        setSearching(true);
        try {
            const response = await axiosInstance.get(
                `/api/crm/contacts/${contactId}/relationships/search?search=${encodeURIComponent(query)}`
            );
            if (response.data.success) {
                setSearchResults(response.data.data);
            }
        } catch (err) {
            console.error('Search error:', err);
        } finally {
            setSearching(false);
        }
    };

    const handleSearchChange = (event, value) => {
        setSearchQuery(value);
        searchContacts(value);
    };

    const handleSelectContact = (event, contact) => {
        if (contact) {
            setFormData(prev => ({
                ...prev,
                related_contact_id: contact.id
            }));
        }
    };

    const handleAddRelationship = async () => {
        if (!formData.related_contact_id || !formData.relationship_type) {
            return;
        }

        setSubmitting(true);
        try {
            const response = await axiosInstance.post(
                `/api/crm/contacts/${contactId}/relationships`,
                formData
            );
            if (response.data.success) {
                setAddDialogOpen(false);
                resetForm();
                fetchRelationships();
            } else {
                setError(response.data.message);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to add relationship');
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateRelationship = async () => {
        if (!selectedRelationship) return;

        setSubmitting(true);
        try {
            const response = await axiosInstance.put(
                `/api/crm/relationships/${selectedRelationship.id}`,
                {
                    relationship_type: formData.relationship_type,
                    description: formData.description
                }
            );
            if (response.data.success) {
                setEditDialogOpen(false);
                fetchRelationships();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update relationship');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteRelationship = async () => {
        if (!selectedRelationship) return;

        setSubmitting(true);
        try {
            const response = await axiosInstance.delete(
                `/api/crm/relationships/${selectedRelationship.id}`
            );
            if (response.data.success) {
                setDeleteDialogOpen(false);
                fetchRelationships();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete relationship');
        } finally {
            setSubmitting(false);
        }
    };

    const openEditDialog = (relationship) => {
        setSelectedRelationship(relationship);
        setFormData({
            relationship_type: relationship.relationship_type,
            description: relationship.description || ''
        });
        setEditDialogOpen(true);
    };

    const openDeleteDialog = (relationship) => {
        setSelectedRelationship(relationship);
        setDeleteDialogOpen(true);
    };

    const resetForm = () => {
        setFormData({
            related_contact_id: null,
            relationship_type: RelationshipType.COLLEAGUE,
            description: '',
            create_reciprocal: false,
            reciprocal_type: ''
        });
        setSearchQuery('');
        setSearchResults([]);
    };

    const getInitials = (name) => {
        if (!name) return '?';
        const parts = name.split(' ');
        return parts.map(p => p[0]).join('').toUpperCase().slice(0, 2);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const allRelationships = [
        ...relationships.from_contact.map(r => ({ ...r, direction: 'from' })),
        ...relationships.to_contact.map(r => ({ ...r, direction: 'to' }))
    ];

    return (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Typography variant="h6">
                    <LinkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Relationships ({allRelationships.length})
                </Typography>
                {canEdit && (
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setAddDialogOpen(true)}
                    >
                        Add Relationship
                    </Button>
                )}
            </Box>

            {allRelationships.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography color="text.secondary">
                        No relationships defined
                    </Typography>
                    {canEdit && (
                        <Button
                            startIcon={<AddIcon />}
                            onClick={() => setAddDialogOpen(true)}
                            sx={{ mt: 2 }}
                        >
                            Add First Relationship
                        </Button>
                    )}
                </Paper>
            ) : (
                <List>
                    {allRelationships.map((rel, index) => {
                        const relatedPerson = rel.direction === 'from' 
                            ? rel.related_contact 
                            : rel.contact;
                        
                        return (
                            <React.Fragment key={rel.id}>
                                {index > 0 && <Divider />}
                                <ListItem
                                    sx={{ 
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: 'action.hover' }
                                    }}
                                    onClick={() => onNavigateToContact?.(relatedPerson?.id)}
                                >
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                                            {getInitials(relatedPerson?.full_name)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="subtitle1">
                                                    {relatedPerson?.full_name || 'Unknown'}
                                                </Typography>
                                                <Chip
                                                    label={RelationshipTypeLabels[rel.relationship_type]}
                                                    size="small"
                                                    variant="outlined"
                                                    icon={rel.direction === 'from' ? <SwapIcon /> : undefined}
                                                />
                                            </Box>
                                        }
                                        secondary={
                                            <>
                                                {relatedPerson?.email && (
                                                    <Typography variant="body2" component="span">
                                                        {relatedPerson.email}
                                                    </Typography>
                                                )}
                                                {rel.description && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                                                        {rel.description}
                                                    </Typography>
                                                )}
                                            </>
                                        }
                                    />
                                    {canEdit && (
                                        <ListItemSecondaryAction>
                                            <Tooltip title="Edit">
                                                <IconButton 
                                                    edge="end" 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openEditDialog(rel);
                                                    }}
                                                    sx={{ mr: 1 }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton 
                                                    edge="end" 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openDeleteDialog(rel);
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </ListItemSecondaryAction>
                                    )}
                                </ListItem>
                            </React.Fragment>
                        );
                    })}
                </List>
            )}

            {/* Add Relationship Dialog */}
            <Dialog 
                open={addDialogOpen} 
                onClose={() => { setAddDialogOpen(false); resetForm(); }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Add Relationship</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <Autocomplete
                            options={searchResults}
                            getOptionLabel={(option) => option.full_name || ''}
                            loading={searching}
                            inputValue={searchQuery}
                            onInputChange={handleSearchChange}
                            onChange={handleSelectContact}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Search Contact"
                                    placeholder="Type to search..."
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                                            {getInitials(option.full_name)}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="body1">{option.full_name}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {option.email}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </li>
                            )}
                            sx={{ mb: 2 }}
                        />

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Relationship Type</InputLabel>
                            <Select
                                value={formData.relationship_type}
                                label="Relationship Type"
                                onChange={(e) => setFormData(prev => ({ 
                                    ...prev, 
                                    relationship_type: e.target.value 
                                }))}
                            >
                                {Object.entries(RelationshipTypeLabels).map(([key, label]) => (
                                    <MenuItem key={key} value={key}>{label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Description (Optional)"
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ 
                                ...prev, 
                                description: e.target.value 
                            }))}
                            multiline
                            rows={2}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setAddDialogOpen(false); resetForm(); }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAddRelationship}
                        disabled={!formData.related_contact_id || submitting}
                    >
                        {submitting ? <CircularProgress size={24} /> : 'Add Relationship'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Relationship Dialog */}
            <Dialog 
                open={editDialogOpen} 
                onClose={() => setEditDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Edit Relationship</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Relationship Type</InputLabel>
                            <Select
                                value={formData.relationship_type}
                                label="Relationship Type"
                                onChange={(e) => setFormData(prev => ({ 
                                    ...prev, 
                                    relationship_type: e.target.value 
                                }))}
                            >
                                {Object.entries(RelationshipTypeLabels).map(([key, label]) => (
                                    <MenuItem key={key} value={key}>{label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ 
                                ...prev, 
                                description: e.target.value 
                            }))}
                            multiline
                            rows={2}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleUpdateRelationship}
                        disabled={submitting}
                    >
                        {submitting ? <CircularProgress size={24} /> : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog 
                open={deleteDialogOpen} 
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Delete Relationship</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this relationship?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteRelationship}
                        disabled={submitting}
                    >
                        {submitting ? <CircularProgress size={24} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ContactRelationships;